// OAuth2 Authorization Page Component
import { escapeHtml } from '../utils/html-utils.js';

class OAuthAuthorize extends HTMLElement {
  constructor() {
    super();
    this.clientInfo = null;
    this.scopes = [];
  }

  connectedCallback() {
    this.loadAuthorizationRequest();
  }

  async loadAuthorizationRequest() {
    try {
      // Get authorization request from session (set by backend)
      const response = await fetch('/api/oauth2/authorize/request', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to load authorization request');
      }

      const data = await response.json();
      this.clientInfo = data.client;
      this.scopes = data.scopes;
      this.render();
    } catch (error) {
      window.logger?.error('Error loading authorization request:', error);
      this.renderError();
    }
  }

  render() {
    if (!this.clientInfo) {
      return;
    }

    this.innerHTML = `
      <div class="oauth-authorize-container">
        <div class="oauth-authorize-card">
          <div class="oauth-authorize-header">
            <h1>Authorize Application</h1>
            <p class="oauth-authorize-subtitle">An application wants to access your Fun Old Times account</p>
          </div>

          <div class="oauth-authorize-client">
            <div class="oauth-authorize-client-info">
              <h2>${escapeHtml(this.clientInfo.name)}</h2>
              <p class="oauth-authorize-client-id">Client ID: ${escapeHtml(this.clientInfo.client_id)}</p>
            </div>
          </div>

          <div class="oauth-authorize-scopes">
            <h3>This application will be able to:</h3>
            ${this.scopes && this.scopes.length > 0 ? `
              <ul class="oauth-authorize-scope-list">
                ${this.scopes.map(scope => this.renderScope(scope)).join('')}
              </ul>
            ` : `
              <p class="oauth-authorize-no-scopes">No specific permissions requested.</p>
            `}
          </div>

          <div class="oauth-authorize-actions">
            <button class="oauth-authorize-btn oauth-authorize-btn-deny" id="deny-btn">
              Deny
            </button>
            <button class="oauth-authorize-btn oauth-authorize-btn-approve" id="approve-btn">
              Authorize
            </button>
          </div>
        </div>
      </div>
    `;

    // Attach event listeners after rendering
    const approveBtn = this.querySelector('#approve-btn');
    const denyBtn = this.querySelector('#deny-btn');
    if (approveBtn) {
      approveBtn.addEventListener('click', () => this.handleApprove());
    }
    if (denyBtn) {
      denyBtn.addEventListener('click', () => this.handleDeny());
    }
  }

  renderScope(scope) {
    const scopeDescriptions = {
      'profile': 'View your profile information (username)',
      'email': 'View your email address',
      'read': 'Read your data',
      'write': 'Modify your data'
    };

    return `
      <li class="oauth-authorize-scope-item">
        <span class="oauth-authorize-scope-icon">✓</span>
        <span class="oauth-authorize-scope-text">${scopeDescriptions[scope] || scope}</span>
      </li>
    `;
  }

  renderError() {
    this.innerHTML = `
      <div class="oauth-authorize-container">
        <div class="oauth-authorize-card">
          <div class="oauth-authorize-error">
            <h2>Error</h2>
            <p>Unable to load authorization request. Please try again.</p>
            <button class="oauth-authorize-btn" onclick="window.location.href='/'">Go Home</button>
          </div>
        </div>
      </div>
    `;
  }

  async handleApprove() {
    try {
      // Submit form to trigger redirect
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/api/oauth2/authorize/approve';
      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      window.logger?.error('Error approving authorization:', error);
      alert('An error occurred while approving the authorization. Please try again.');
    }
  }

  async handleDeny() {
    try {
      // Submit form to trigger redirect
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/api/oauth2/authorize/deny';
      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      window.logger?.error('Error denying authorization:', error);
      alert('An error occurred while denying the authorization. Please try again.');
    }
  }

}

customElements.define('oauth-authorize', OAuthAuthorize);

